import React, { useEffect, useState, useCallback, useMemo } from "react";
import styles from "./UtilityBar.module.scss";
import { buttonTexts, filterList, filterNames } from "./constants";
import InputField from "../../../reusable-components/TNInputField";
import TNButton from "../../../reusable-components/TNButton";
import {
  ButtonShape,
  ButtonSize,
  ButtonType,
} from "../../../enums/ButtonAttributesEnums";
import LienProcessingService from "../../../services/lienProcess.service";
import { useFormContext, useWatch } from "react-hook-form";
import { useLienProcessFilterContext } from "../../../context/LienProcessFilterContext";
import { getFilteredOptions } from "../../../utils/getDropdownOptions";

interface UtilityBarInterface {
  editAccess: boolean;
  selectedRowsKeyLength: number;
  handleSyncConfirm: () => void;
  handleDownloadAll: () => void;
  handleExportUnsynced: () => void;
}

const UtilityBar = ({
  editAccess,
  selectedRowsKeyLength,
  handleSyncConfirm,
  handleDownloadAll,
  handleExportUnsynced,
}: UtilityBarInterface) => {
  const { DOWNLOAD_BTN, SYNC_RECORDS_BTN, EXPORT_RECORD_BTN } = buttonTexts;
  const { watch, setValue, getValues } = useFormContext();
  const { filters, updateFilters } = useLienProcessFilterContext();
  const selectionOrder = filters?.selectionOrder ?? [];
  const [customerSearch, setCustomerSearch] = useState("");
  const [jobSearch, setJobSearch] = useState("");
  const [monthSearch, setMonthSearch] = useState("");
  const selectedCustomers = useWatch({ name: filterNames.CUSTOMER_FIELD });
  const selectedJobs = useWatch({ name: filterNames.JOB_FIELD });
  const selectedMonths = useWatch({ name: filterNames.MONTH_FIELD });

  const {
    FetchLienCustomerFilterMeta,
    FetchLienJobFilterMeta,
    FetchLienMonthFilterMeta,
  } = LienProcessingService();

  const { data: customerList, refetch: refetchCustomerList } =
    FetchLienCustomerFilterMeta({
      jobId: selectedJobs,
      month: selectedMonths,
    });
  const { data: jobList, refetch: refetchJobList } = FetchLienJobFilterMeta({
    companyId: selectedCustomers,
    month: selectedMonths,
  });
  const { data: monthList, refetch: refetchMonthList } =
    FetchLienMonthFilterMeta({
      companyId: selectedCustomers,
      jobId: selectedJobs,
    });

  const refetchMap = useMemo(
    () => ({
      [filterNames.CUSTOMER_FIELD]: refetchCustomerList,
      [filterNames.JOB_FIELD]: refetchJobList,
      [filterNames.MONTH_FIELD]: refetchMonthList,
    }),
    [refetchCustomerList, refetchJobList, refetchMonthList]
  );

  const customerOptions = useMemo(
    () =>
      customerList?.customers?.map(({ companyId, companyName }) => ({
        value: companyId,
        label: companyName,
      })) || [],
    [customerList]
  );
  const jobOptions = useMemo(
    () =>
      jobList?.jobs?.map(({ jobId, jobName }) => ({
        value: jobId,
        label: jobName,
      })) || [],
    [jobList]
  );
  const monthOptions = useMemo(
    () =>
      monthList?.months?.map((month) => ({
        value: month,
        label: month,
      })) || [],
    [monthList]
  );

  const filteredCustomerOptions = useMemo(
    () => getFilteredOptions(customerOptions, customerSearch),
    [customerOptions, customerSearch]
  );
  const filteredJobOptions = useMemo(
    () => getFilteredOptions(jobOptions, jobSearch),
    [jobOptions, jobSearch]
  );
  const filteredMonthOptions = useMemo(
    () => getFilteredOptions(monthOptions, monthSearch),
    [monthOptions, monthSearch]
  );

  const handleChangeFactory = useCallback(
    (itemName) => (newValue) => {
      setValue(itemName, newValue);
      updateFilters({ [itemName]: newValue, page: 1 });

      const newOrder = updateSelectionOrder(selectionOrder, itemName, newValue);
      if (JSON.stringify(newOrder) !== JSON.stringify(selectionOrder)) {
        updateFilters({ selectionOrder: newOrder });
      }
    },
    [setValue, updateFilters, getValues, selectionOrder]
  );

  const updateSelectionOrder = (order: string[], itemName: string, newValue: any) => {
    if (!newValue || (Array.isArray(newValue) && newValue.length === 0)) {
      if (order.at(0) === itemName && order.length > 1) return order;
      if (order.at(1) === itemName && order.length > 2) return order;
      if (order.at(2) === itemName) {
        const firstVal = getValues(order.at(0));
        const secondVal = getValues(order.at(1));
        const isEmpty = (val) =>
          !val || (Array.isArray(val) && val.length === 0);

        if (isEmpty(secondVal) && isEmpty(firstVal)) return [];
        if (isEmpty(secondVal)) return [order[0]];
      }
      if (order.at(1) === itemName) {
        const firstVal = getValues(order.at(0));
        if (!firstVal || (Array.isArray(firstVal) && firstVal.length === 0))
          return [];
      }
      return order.filter((name) => name !== itemName);
    }
    if (!order.includes(itemName)) {
      return [...order, itemName];
    }
    return order;
  };


  useEffect(() => {
    if (selectionOrder?.length === 0) {
      refetchCustomerList();
      refetchJobList();
      refetchMonthList();
    }
  }, [selectionOrder, refetchCustomerList, refetchJobList, refetchMonthList]);

  useEffect(() => {
    const priority = selectionOrder?.indexOf(filterNames.CUSTOMER_FIELD);
    if (priority === -1) return;

    if (priority === 0) {
      if (selectionOrder?.length === 3) {
        const p2Name = selectionOrder[1];
        const p3Name = selectionOrder[2];
        if (p2Name) refetchMap[p2Name]();
        if (p3Name) refetchMap[p3Name]();
      } else if (selectionOrder?.length === 2) {
        const p2Name = selectionOrder[1];
        if (p2Name) refetchMap[p2Name]();
      } else {
        refetchMap[filterNames.JOB_FIELD]();
        refetchMap[filterNames.MONTH_FIELD]();
      }
    } else if (priority === 1) {
      if (selectionOrder?.length === 3) {
        const p3Name = selectionOrder[2];
        if (p3Name) refetchMap[p3Name]();
      } else {
        const allFilters = [
          filterNames.CUSTOMER_FIELD,
          filterNames.JOB_FIELD,
          filterNames.MONTH_FIELD,
        ];
        const remainingFilter = allFilters.find(
          (name) => !selectionOrder?.includes(name)
        );
        if (remainingFilter) {
          refetchMap[remainingFilter]();
        }
      }
    }
  }, [selectedCustomers, selectionOrder, refetchMap]);

  useEffect(() => {
    const priority = selectionOrder?.indexOf(filterNames.JOB_FIELD);
    if (priority === -1) return;

    if (priority === 0) {
      if (selectionOrder?.length === 3) {
        const p2Name = selectionOrder[1];
        const p3Name = selectionOrder[2];
        if (p2Name) refetchMap[p2Name]();
        if (p3Name) refetchMap[p3Name]();
      } else if (selectionOrder?.length === 2) {
        const p2Name = selectionOrder[1];
        if (p2Name) refetchMap[p2Name]();
      } else {
        refetchMap[filterNames.CUSTOMER_FIELD]();
        refetchMap[filterNames.MONTH_FIELD]();
      }
    } else if (priority === 1) {
      if (selectionOrder?.length === 3) {
        const p3Name = selectionOrder[2];
        if (p3Name) refetchMap[p3Name]();
      } else {
        const allFilters = [
          filterNames.CUSTOMER_FIELD,
          filterNames.JOB_FIELD,
          filterNames.MONTH_FIELD,
        ];
        const remainingFilter = allFilters.find(
          (name) => !selectionOrder?.includes(name)
        );
        if (remainingFilter) {
          refetchMap[remainingFilter]();
        }
      }
    }
  }, [selectedJobs, selectionOrder, refetchMap]);

  useEffect(() => {
    const priority = selectionOrder?.indexOf(filterNames.MONTH_FIELD);
    if (priority === -1) return;

    if (priority === 0) {
      if (selectionOrder?.length === 3) {
        const p2Name = selectionOrder[1];
        const p3Name = selectionOrder[2];
        if (p2Name) refetchMap[p2Name]();
        if (p3Name) refetchMap[p3Name]();
      } else if (selectionOrder?.length === 2) {
        const p2Name = selectionOrder[1];
        if (p2Name) refetchMap[p2Name]();
      } else {
        refetchMap[filterNames.CUSTOMER_FIELD]();
        refetchMap[filterNames.JOB_FIELD]();
      }
    } else if (priority === 1) {
      if (selectionOrder?.length === 3) {
        const p3Name = selectionOrder[2];
        if (p3Name) refetchMap[p3Name]();
      } else {
        const allFilters = [
          filterNames.CUSTOMER_FIELD,
          filterNames.JOB_FIELD,
          filterNames.MONTH_FIELD,
        ];
        const remainingFilter = allFilters.find(
          (name) => !selectionOrder?.includes(name)
        );
        if (remainingFilter) {
          refetchMap[remainingFilter]();
        }
      }
    }
  }, [selectedMonths, selectionOrder, refetchMap]);

  const filterOptionsList = filterList({
    customerOptions: filteredCustomerOptions,
    jobOptions: filteredJobOptions,
    monthOptions: filteredMonthOptions,
    customerSearch,
    jobSearch,
    monthSearch,
    setCustomerSearch,
    setJobSearch,
    setMonthSearch,
  });

  // Populate fields from context on mount
  useEffect(() => {
    if (filters?.customerFilter) setValue(filterNames.CUSTOMER_FIELD, filters.customerFilter);
    if (filters?.jobFilter) setValue(filterNames.JOB_FIELD, filters.jobFilter);
    if (filters?.monthFilter) setValue(filterNames.MONTH_FIELD, filters.monthFilter);
  }, []);

  useEffect(() => {
    const fieldName = filterNames.CUSTOMER_FIELD;
    const selected = getValues(fieldName) ?? [];
    if (customerOptions?.length > 0 && selected?.length > 0) {
      const validIds = new Set(customerOptions?.map((opt) => opt?.value));
      const newSelected = selected?.filter((id) => validIds?.has(id));
      if (newSelected?.length !== selected?.length) {
        handleChangeFactory(fieldName)(newSelected);
      }
    }
  }, [customerOptions]);

  useEffect(() => {
    const fieldName = filterNames.JOB_FIELD;
    const selected = getValues(fieldName) ?? [];
    if (jobOptions?.length > 0 && selected?.length > 0) {
      const validIds = new Set(jobOptions?.map((opt) => opt?.value));
      const newSelected = selected?.filter((id) => validIds?.has(id));
      if (newSelected?.length !== selected?.length) {
        handleChangeFactory(fieldName)(newSelected);
      }
    }
  }, [jobOptions]);

  useEffect(() => {
    const fieldName = filterNames.MONTH_FIELD;
    const selected = getValues(fieldName) ?? [];
    if (monthOptions?.length > 0 && selected?.length > 0) {
      const validIds = new Set(monthOptions?.map((opt) => opt?.value));
      const newSelected = selected?.filter((id) => validIds?.has(id));
      if (newSelected?.length !== selected?.length) {
        handleChangeFactory(fieldName)(newSelected);
      }
    }
  }, [monthOptions]);



  useEffect(() => {
    if (selectedCustomers?.length === 0) {
      const newOrder = selectionOrder?.filter((val) => val !== filterNames.CUSTOMER_FIELD);
      if (JSON.stringify(newOrder) !== JSON.stringify(selectionOrder)) {
        updateFilters({ selectionOrder: newOrder });
      }
    }
  }, [selectedCustomers]);
  useEffect(() => {
    if (selectedJobs?.length === 0) {
      const newOrder = selectionOrder?.filter((val) => val !== filterNames.JOB_FIELD);
      if (JSON.stringify(newOrder) !== JSON.stringify(selectionOrder)) {
        updateFilters({ selectionOrder: newOrder });
      }
    }
  }, [selectedJobs]);
  useEffect(() => {
    if (selectedMonths?.length === 0) {
      const newOrder = selectionOrder?.filter((val) => val !== filterNames.MONTH_FIELD);
      if (JSON.stringify(newOrder) !== JSON.stringify(selectionOrder)) {
        updateFilters({ selectionOrder: newOrder });
      }
    }
  }, [selectedMonths]);

  return (
    <div className={styles.headerSection}>
      <div className={styles.leftContainer}>
        {filterOptionsList.map((item) => (
          <InputField
            key={item.name}
            type={item.type}
            name={item.name}
            bordered={item.bordered}
            placeholder={item.placeholder}
            options={item.options}
            values={watch(item.name)}
            onChange={handleChangeFactory(item.name)}
            resetCheckbox={() => handleChangeFactory(item.name)([])}
            setSearch={item.setSearch}
            searchValue={item.searchValue}
          />
        ))}
      </div>
      <div className={styles.rightContainer}>
        <TNButton
          type={ButtonType.DEFAULT}
          shape={ButtonShape.DEFAULT}
          size={ButtonSize.LARGE}
          text={EXPORT_RECORD_BTN}
          onClick={handleExportUnsynced}
          disabled={!editAccess}
        />
        <TNButton
          type={ButtonType.DEFAULT}
          shape={ButtonShape.DEFAULT}
          size={ButtonSize.LARGE}
          text={DOWNLOAD_BTN}
          onClick={handleDownloadAll}
          disabled={!editAccess || !!!selectedRowsKeyLength}
        />
        <TNButton
          shape={ButtonShape.DEFAULT}
          size={ButtonSize.LARGE}
          text={SYNC_RECORDS_BTN}
          onClick={handleSyncConfirm}
          disabled={!editAccess}
        />
      </div>
    </div>
  );
};

export default UtilityBar;
